import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { FaUser, FaPlus, FaTrash, FaStar, FaMapMarkerAlt, FaTheaterMasks, FaPlay, FaBook, FaEdit } from 'react-icons/fa';
import { API_ENDPOINTS } from '../config/backendConfig';
import CharacterPreviewModal from './CharacterPreviewModal';
import CharacterEditModal from './CharacterEditModal';
import { auth } from '../firebase';
import homepageBackground from '../assets/Homepageback.png';
import splashImage from '../assets/splash.png';
import CustomModal from './CustomModal';
import { getCache, setCache, createCacheKey, clearCache } from '../utils/cache';
import { enqueueAndPoll } from '../utils/jobPolling';

const GENRES = [
  'Adventure', 'Fantasy', 'Mystery', 'Comedy', 'Action', 'Romance', 
  'Sci-Fi', 'Horror', 'Drama', 'Thriller', 'Animation', 'Family'
];

// Removed MOODS as Mood selection is no longer part of Step 4

const ROLES = [
  'Hero/Protagonist', 'Villain/Antagonist', 'Sidekick', 'Mentor', 
  'Love Interest', 'Comic Relief', 'Guardian', 'Rival', 'Friend', 'Family Member'
];

export default function StorySetupModal({ isOpen, onClose, onStorySetupComplete }) {
  const [currentStep, setCurrentStep] = useState(1);
  const [storyTitle, setStoryTitle] = useState('');
  const [mainCharacter, setMainCharacter] = useState({ 
    name: '', 
    age: '', 
    gender: '', 
    role: 'Hero/Protagonist', 
    appearance: '',
    countryOfOrigin: '', // Add country of origin field
    characterFace: '' // Add character face field
  });
  const [additionalCharacters, setAdditionalCharacters] = useState([]);
  const [storyLocation, setStoryLocation] = useState('');
  const [selectedGenre, setSelectedGenre] = useState('');
  // Removed selectedMood state as Mood selection is removed
  const [showAddCharacter, setShowAddCharacter] = useState(false);
  const [newCharacter, setNewCharacter] = useState({ name: '', age: '', gender: '', role: '', appearance: '' });
  const [storySummary, setStorySummary] = useState('');
  
  // New state for character creation functionality
  const [showCharacterPreview, setShowCharacterPreview] = useState(false);
  const [characterImage, setCharacterImage] = useState(null);
  const [isGeneratingCharacter, setIsGeneratingCharacter] = useState(false);
  const [characterCreationError, setCharacterCreationError] = useState('');
  const [showSuccessNotification, setShowSuccessNotification] = useState(false);
  const [showCharacterLibrary, setShowCharacterLibrary] = useState(false);
  
  // Character library states
  const [userCharacters, setUserCharacters] = useState([]);
  const [characterLibraryLoading, setCharacterLibraryLoading] = useState(false);
  const [characterLibraryError, setCharacterLibraryError] = useState(null);
  const [selectedCharacterFromLibrary, setSelectedCharacterFromLibrary] = useState(null);
  const [showEditModalForSelected, setShowEditModalForSelected] = useState(false);

  // Add another character flow states
  const [showAddAnotherCharacter, setShowAddAnotherCharacter] = useState(false);
  const [characterConfirmed, setCharacterConfirmed] = useState(false);
  
  // Track selected characters for cumulative selection
  const [selectedCharacters, setSelectedCharacters] = useState([]);
  
  // Confirmation modal state
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [characterToDelete, setCharacterToDelete] = useState(null);

  const totalSteps = 4;

  const handleAddCharacter = () => {
    if (newCharacter.name && newCharacter.age && newCharacter.gender && newCharacter.role && newCharacter.appearance) {
      setAdditionalCharacters([...additionalCharacters, { ...newCharacter, id: Date.now() }]);
      setNewCharacter({ name: '', age: '', gender: '', role: '', appearance: '' });
      setShowAddCharacter(false);
    }
  };

  const handleRemoveCharacter = (characterId) => {
    setAdditionalCharacters(additionalCharacters.filter(char => char.id !== characterId));
  };

  // Handle removing characters from selected characters list
  const handleRemoveSelectedCharacter = (characterToRemove) => {
    setSelectedCharacters(prev => prev.filter(char => 
      char.id !== characterToRemove.id && 
      char.characterId !== characterToRemove.characterId &&
      char.name !== characterToRemove.name
    ));
  };

  const handleNext = () => {
    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleComplete = async () => {
    try {
      // If we have a character with an image, save it to Firebase
      if (mainCharacter.imageUrl) {
        const characterData = {
          name: mainCharacter.name,
          age: mainCharacter.age,
          gender: mainCharacter.gender,
          role: mainCharacter.role,
          description: mainCharacter.appearance,
          imageUrl: mainCharacter.imageUrl,
          countryOfOrigin: mainCharacter.countryOfOrigin || 'unknown',
          characterFace: mainCharacter.characterFace || '', // Add character face details
          // Extract appearance details from the appearance field
          hairColor: extractHairColor(mainCharacter.appearance),
          hairStyle: extractHairStyle(mainCharacter.appearance),
          eyeColor: extractEyeColor(mainCharacter.appearance),
          skinTone: extractSkinTone(mainCharacter.appearance),
          height: extractHeight(mainCharacter.appearance),
          build: extractBuild(mainCharacter.appearance),
          clothing: {
            top: extractClothingTop(mainCharacter.appearance),
            bottom: extractClothingBottom(mainCharacter.appearance),
            accessories: [extractAccessories(mainCharacter.appearance)].filter(item => item !== 'unknown'),
            colors: [extractColors(mainCharacter.appearance)].filter(color => color !== 'unknown'),
            style: 'casual'
          },
          distinctiveFeatures: [extractDistinctiveFeatures(mainCharacter.appearance)].filter(feature => feature !== 'unknown'),
          personality: extractPersonality(mainCharacter.appearance),
          paintingStyle: 'Comforting Old french children book illustration', // Always set default style
          generatedAt: mainCharacter.generatedAt
        };

        // Save character to Firebase (we'll need to get the storyId from the parent component)
        // For now, we'll pass the character data to the parent
        const allCharacters = [characterData, ...additionalCharacters];
        
        onStorySetupComplete({
          storyTitle,
          characters: allCharacters,
          location: storyLocation,
          genre: selectedGenre,
          storySummary,
          mainCharacterWithImage: characterData // Pass the main character with image data
        });
      } else {
        // Fallback to original behavior if no character image
        const allCharacters = [mainCharacter, ...additionalCharacters];
        onStorySetupComplete({
          storyTitle,
          characters: allCharacters,
          location: storyLocation,
          genre: selectedGenre,
          storySummary
        });
      }
    } catch (error) {
      console.error('Error completing story setup:', error);
      // Still call onStorySetupComplete even if character save fails
      const allCharacters = [mainCharacter, ...additionalCharacters];
      onStorySetupComplete({
        storyTitle,
        characters: allCharacters,
        location: storyLocation,
        genre: selectedGenre,
        storySummary
      });
    }
  };

  const canProceed = () => {
    switch (currentStep) {
      case 1: return storyTitle.trim() !== '';
      case 2: 
        // Can proceed if either a character is confirmed from library OR a character is created with BFL
        return (characterConfirmed && selectedCharacterFromLibrary) || 
               (mainCharacter.name && mainCharacter.age && mainCharacter.gender && mainCharacter.appearance && mainCharacter.imageUrl && mainCharacter.countryOfOrigin && mainCharacter.characterFace);
      case 3: return additionalCharacters.length > 0 || true; // Can proceed even without additional characters
      case 4: return storyLocation.trim() !== '' && !!selectedGenre && storySummary.trim() !== '';
      default: return false;
    }
  };

  const handleCreateCharacter = async () => {
    console.log('=== FUNCTION CALLED ===');
    console.log('=== FUNCTION CALLED ===');
    console.log('=== FUNCTION CALLED ===');
    console.log('mainCharacter:', mainCharacter);
    console.log('Validation check:', {
      name: !!mainCharacter.name,
      age: !!mainCharacter.age,
      gender: !!mainCharacter.gender,
      appearance: !!mainCharacter.appearance,
      countryOfOrigin: !!mainCharacter.countryOfOrigin,
      characterFace: !!mainCharacter.characterFace
    });
    
    if (!mainCharacter.name || !mainCharacter.age || !mainCharacter.gender || !mainCharacter.appearance || !mainCharacter.countryOfOrigin || !mainCharacter.characterFace) {
      console.log('=== VALIDATION FAILED ===');
      setCharacterCreationError('Please fill in all character details including country of origin and character face before creating the character.');
      return;
    }

    setIsGeneratingCharacter(true);
    setCharacterCreationError(null);

    try {
      // Create a simple character object with appearance as string
      const characterData = {
        name: mainCharacter.name,
        age: mainCharacter.age,
        gender: mainCharacter.gender,
        role: mainCharacter.role,
        description: mainCharacter.appearance, // Pass appearance as simple string
        countryOfOrigin: mainCharacter.countryOfOrigin || 'unknown',
        characterFace: mainCharacter.characterFace || '',
        // Remove all extracted fields to keep it simple
        hairColor: 'unknown',
        hairStyle: 'unknown',
        eyeColor: 'unknown',
        skinTone: 'unknown',
        height: 'unknown',
        build: 'unknown',
        clothing: {
          top: 'unknown',
          bottom: 'unknown',
          accessories: [],
          colors: [],
          style: 'casual'
        },
        distinctiveFeatures: [],
        personality: '',
        paintingStyle: 'oil painting'
      };

      // Non-blocking: enqueue job, poll until done (same as art history / ways of seeing).
      console.log('=== CHARACTER CREATION DEBUG (StorySetup) ===');
      const data = await enqueueAndPoll(
        `${API_ENDPOINTS.backendBase}/api/characters/generate-image`,
        {
          characterData: { ...characterData, userId: auth.currentUser?.uid },
          prompt: null,
          storyId: null,
        },
        { interval: 2000, maxAttempts: 150 }
      );
      console.log('StorySetupModal - BFL response data:', data);
      const imageUrl = data?.imageUrl || data?.result?.imageUrl;
      if (!imageUrl) {
        setCharacterCreationError('Character was created but image could not be loaded. Check your library.');
        return;
      }
      setCharacterImage(imageUrl);
      setMainCharacter((prev) => ({ ...prev, imageUrl }));
      setShowCharacterPreview(true);

    } catch (error) {
      console.error('Error creating character:', error);
      if (error.name === 'AbortError') {
        setCharacterCreationError('Character generation timed out. Please try again - this can take up to 2 minutes.');
      } else {
        setCharacterCreationError('Failed to create character. Please try again.');
      }
    } finally {
      setIsGeneratingCharacter(false);
    }
  };

  const handleCharacterPreviewSave = async () => {
    try {
      // Save character data to the main character state
      const savedCharacter = {
        ...mainCharacter,
        imageUrl: characterImage,
        generatedAt: new Date().toISOString()
      };
      
      setMainCharacter(savedCharacter);
      setShowCharacterPreview(false);
      setCharacterImage(null);
      
      // Character is already saved to library by the backend during image generation.
      // Invalidate cache so "My Character Library" shows the new character next time.
      if (auth.currentUser) {
        clearCache(createCacheKey('characters', { library: auth.currentUser.uid }, auth.currentUser.uid));
      }
      
      // Show success message with beautiful notification
      setShowSuccessNotification(true);
      setTimeout(() => setShowSuccessNotification(false), 5000); // Auto-hide after 5 seconds
      
    } catch (error) {
      console.error('Error saving character:', error);
      setCharacterCreationError('Failed to save character. Please try again.');
    }
  };

  const handleCharacterPreviewEdit = async (editPrompt) => {
    try {
      setIsGeneratingCharacter(true);

      const data = await enqueueAndPoll(
        `${API_ENDPOINTS.backendBase}/api/characters/generate-image`,
        {
          characterData: { ...mainCharacter, userId: auth.currentUser?.uid },
          prompt: editPrompt,
          storyId: null,
        },
        { interval: 2000, maxAttempts: 150 }
      );
      setCharacterImage(data.imageUrl);
      
    } catch (error) {
      console.error('Error editing character:', error);
      setCharacterCreationError('Failed to edit character. Please try again.');
    } finally {
      setIsGeneratingCharacter(false);
    }
  };

  const handleCharacterPreviewRegenerate = async (prompt) => {
    await handleCharacterPreviewEdit(prompt);
  };

  // Helper functions to extract character details from appearance text
  const extractHairColor = (appearance) => {
    const hairColors = ['black', 'brown', 'blonde', 'red', 'gray', 'white', 'auburn', 'chestnut'];
    for (const color of hairColors) {
      if (appearance.toLowerCase().includes(color)) {
        return color;
      }
    }
    return 'unknown';
  };

  const extractHairStyle = (appearance) => {
    const hairStyles = ['long', 'short', 'curly', 'straight', 'wavy', 'braided', 'ponytail'];
    for (const style of hairStyles) {
      if (appearance.toLowerCase().includes(style)) {
        return style;
      }
    }
    return 'unknown';
  };

  const extractEyeColor = (appearance) => {
    const eyeColors = ['blue', 'brown', 'green', 'hazel', 'gray', 'black'];
    for (const color of eyeColors) {
      if (appearance.toLowerCase().includes(color)) {
        return color;
      }
    }
    return 'unknown';
  };

  const extractSkinTone = (appearance) => {
    const skinTones = ['fair', 'light', 'medium', 'dark', 'olive', 'tan'];
    for (const tone of skinTones) {
      if (appearance.toLowerCase().includes(tone)) {
        return tone;
      }
    }
    return 'unknown';
  };

  const extractHeight = (appearance) => {
    const heights = ['tall', 'short', 'average', 'medium'];
    for (const height of heights) {
      if (appearance.toLowerCase().includes(height)) {
        return height;
      }
    }
    return 'unknown';
  };

  const extractBuild = (appearance) => {
    const builds = ['slim', 'athletic', 'muscular', 'curvy', 'thin', 'stocky'];
    for (const build of builds) {
      if (appearance.toLowerCase().includes(build)) {
        return build;
      }
    }
    return 'unknown';
  };

  const extractClothingTop = (appearance) => {
    const tops = ['shirt', 't-shirt', 'blouse', 'sweater', 'jacket', 'dress'];
    for (const top of tops) {
      if (appearance.toLowerCase().includes(top)) {
        return top;
      }
    }
    return 'unknown';
  };

  const extractClothingBottom = (appearance) => {
    const bottoms = ['pants', 'jeans', 'shorts', 'skirt', 'dress'];
    for (const bottom of bottoms) {
      if (appearance.toLowerCase().includes(bottom)) {
        return bottom;
      }
    }
    return 'unknown';
  };

  const extractAccessories = (appearance) => {
    const accessories = ['glasses', 'hat', 'scarf', 'necklace', 'earrings', 'watch', 'belt'];
    const found = [];
    for (const accessory of accessories) {
      if (appearance.toLowerCase().includes(accessory)) {
        found.push(accessory);
      }
    }
    return found;
  };

  const extractColors = (appearance) => {
    const colors = ['red', 'blue', 'green', 'yellow', 'purple', 'orange', 'pink', 'black', 'white', 'gray', 'brown'];
    const found = [];
    for (const color of colors) {
      if (appearance.toLowerCase().includes(color)) {
        found.push(color);
      }
    }
    return found;
  };

  const extractDistinctiveFeatures = (appearance) => {
    const features = ['freckles', 'scar', 'birthmark', 'dimples', 'beard', 'mustache', 'glasses'];
    const found = [];
    for (const feature of features) {
      if (appearance.toLowerCase().includes(feature)) {
        found.push(feature);
      }
    }
    return found;
  };

  const extractPersonality = (appearance) => {
    const personalityKeywords = ['confident', 'shy', 'outgoing', 'introverted', 'brave', 'cautious', 'friendly', 'serious', 'playful', 'mature', 'childlike', 'wise', 'naive', 'experienced', 'innocent'];
    for (const keyword of personalityKeywords) {
      if (appearance.toLowerCase().includes(keyword)) {
        return keyword;
      }
    }
    return 'unknown';
  };

  // Load user's character library. Pass skipCache=true when opening the modal so newly created characters show.
  const loadUserCharacterLibrary = async (skipCache = false) => {
    if (!auth.currentUser) return;
    
    const cacheKey = createCacheKey('characters', { library: auth.currentUser.uid }, auth.currentUser.uid);
    if (!skipCache) {
      const cached = getCache(cacheKey);
      if (cached) {
        console.log('✅ [STORY] Using cached character library:', cached.length);
        setUserCharacters(cached);
        setCharacterLibraryError(null);
        return;
      }
    } else {
      clearCache(cacheKey);
    }
    
    setCharacterLibraryLoading(true);
    setCharacterLibraryError(null);
    try {
      console.log('Loading character library for user:', auth.currentUser.uid);
      const response = await fetch(`${API_ENDPOINTS.getUserLibrary}/${auth.currentUser.uid}`);
      
      if (response.ok) {
        const data = await response.json();
        console.log('Character library response:', data);
        const charactersData = data.characters || [];
        
        setCache(cacheKey, charactersData, 5 * 60 * 1000);
        setUserCharacters(charactersData);
        console.log('Loaded user characters:', charactersData.length);
      } else {
        const errText = await response.text();
        console.error('Failed to load character library:', response.status, errText);
        setCharacterLibraryError('Could not load your characters. Please try again.');
      }
    } catch (error) {
      console.error('Error loading character library:', error);
      setCharacterLibraryError('Could not load your characters. Check your connection and try again.');
    } finally {
      setCharacterLibraryLoading(false);
    }
  };

  // Handle character selection from library
  const handleCharacterSelectionFromLibrary = (character) => {
    console.log('=== CHARACTER LIBRARY SELECTION ===');
    console.log('Selected character:', character);
    console.log('Current selectedCharacters:', selectedCharacters);
    
    setSelectedCharacterFromLibrary(character);
    setShowCharacterLibrary(false);
    // Do not auto-confirm on selection; require explicit confirmation
    setCharacterConfirmed(false);
    
    // Add character to the selected characters list (don't replace existing ones)
    setSelectedCharacters(prev => {
      const updated = [...prev, character];
      console.log('Updated selectedCharacters:', updated);
      return updated;
    });
    
    // Auto-fill the character form with selected character data
    if (character) {
      setMainCharacter({
        name: character.name || '',
        age: character.age || '',
        gender: character.gender || '',
        role: character.role || 'Hero/Protagonist',
        countryOfOrigin: character.countryOfOrigin || '',
        characterFace: character.characterFace || '',
        appearance: character.appearance || ''
      });
    }
  };

  // Handle character deletion from library
  const handleDeleteCharacterFromLibrary = async (characterId, characterName) => {
    console.log('=== DELETE CHARACTER FROM LIBRARY ===');
    console.log('Character ID:', characterId);
    console.log('Character Name:', characterName);
    
    // Set the character to delete and show confirmation modal
    setCharacterToDelete({ id: characterId, name: characterName });
    setShowDeleteConfirmation(true);
  };

  const confirmDeleteCharacter = async () => {
    if (!characterToDelete) return;
    
    const { id: characterId, name: characterName } = characterToDelete;
    
    try {
      console.log('Making DELETE request to:', `${API_ENDPOINTS.backendBase}/api/characters/${characterId}`);
      const response = await fetch(`${API_ENDPOINTS.backendBase}/api/characters/${characterId}`, {
        method: 'DELETE'
      });

      console.log('DELETE response status:', response.status);
      console.log('DELETE response ok:', response.ok);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('DELETE response error:', errorText);
        throw new Error(`Failed to delete character: ${response.status} ${errorText}`);
      }

      // Remove the character from the local state
      setUserCharacters(prev => {
        const filtered = prev.filter(char => (char.id !== characterId && char.characterId !== characterId));
        console.log('Updated userCharacters:', filtered);
        return filtered;
      });
      
      console.log('Character deleted successfully:', characterName);
      
      // Show success message with custom modal
      setCharacterToDelete({ id: null, name: characterName, success: true });
      setShowDeleteConfirmation(true);
      
    } catch (error) {
      console.error('Error deleting character:', error);
      // Show error message with custom modal
      setCharacterToDelete({ id: null, name: characterName, error: true });
      setShowDeleteConfirmation(true);
    }
  };

  // Handle add another character decision
  const handleAddAnotherCharacterDecision = (addAnother) => {
    setShowAddAnotherCharacter(false);
    if (addAnother) {
      // Keep existing character selections and just reset the form for new character
      // Don't reset selectedCharacters - preserve existing selections
      setCharacterConfirmed(false);
      setSelectedCharacterFromLibrary(null);
      setMainCharacter({ 
        name: '', 
        age: '', 
        gender: '', 
        role: 'Hero/Protagonist', 
        appearance: '',
        countryOfOrigin: '',
        characterFace: ''
      });
      // Keep the character library open for additional selections
      setShowCharacterLibrary(true);
    } else {
      // Navigate directly to Step 4 (Story Details) with all selected characters preserved
      console.log('Navigating to Step 4 with selected characters:', selectedCharacters);
      setCurrentStep(4);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-2 sm:p-3 md:px-[2cm] md:py-6 overflow-y-auto overflow-x-hidden" style={{ backgroundColor: 'transparent', background: 'none' }}>
      {/* Background Image - Using Homepage Background */}
      <div 
        className="fixed inset-0"
        style={{ 
          backgroundImage: `url(${homepageBackground}) !important`,
          backgroundSize: 'cover !important',
          backgroundPosition: 'center !important',
          backgroundRepeat: 'no-repeat !important',
          backgroundAttachment: 'fixed !important',
          backgroundColor: 'transparent !important',
          zIndex: 0,
          width: '100vw',
          height: '100vh',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0
        }}
      />
      
      {/* Splash images merged into background */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none" style={{ zIndex: 0 }}>
        <img 
          src={splashImage} 
          alt="" 
          className="absolute top-10 right-5 opacity-20"
          style={{ 
            width: '400px',
            height: '200px',
            objectFit: 'cover',
            transform: 'rotate(15deg)',
            mixBlendMode: 'multiply',
            filter: 'blur(2px)'
          }}
        />
        <img 
          src={splashImage} 
          alt="" 
          className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 opacity-15"
          style={{ 
            width: '500px',
            height: '300px',
            objectFit: 'cover',
            transform: 'translate(-50%, -50%) rotate(-10deg)',
            mixBlendMode: 'multiply',
            filter: 'blur(3px)'
          }}
        />
      </div>
      
      {/* Light overlay for better text readability */}
      <div className="fixed inset-0" style={{ backgroundColor: 'rgba(0, 0, 0, 0.1)', zIndex: 1 }} />

      {/* Creating character overlay — full-screen loader while job is running */}
      {isGeneratingCharacter && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 text-center">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-slate-100 mb-6">
              <div className="animate-spin rounded-full h-8 w-8 border-2 border-slate-500 border-t-transparent" />
            </div>
            <h3 className="text-xl font-bold text-gray-800 mb-2">Awesome!</h3>
            <p className="text-gray-600">Give a moment — let Artmosphere create your character.</p>
          </div>
        </div>
      )}

      <div className={`bg-white rounded-none sm:rounded-xl md:rounded-2xl shadow-2xl w-full overflow-hidden flex flex-col relative z-10 ${currentStep === 1 ? 'max-w-2xl md:max-w-3xl max-h-[90dvh] md:max-h-[85vh] my-auto' : 'max-w-7xl h-full max-h-[100dvh] md:max-h-[90vh] min-h-0'}`}>
        {/* Compact header — no amber, aesthetic like other pages */}
        <div className="flex-shrink-0 bg-white border-b border-gray-200 shadow-sm">
          <div className="flex items-center justify-between py-2.5 md:py-3 px-4 md:px-6 min-h-[52px]">
            <div className="flex items-center gap-3 min-w-0">
              <span className="text-lg" aria-hidden>🎬</span>
              <div className="min-w-0">
                <h2 className="text-base md:text-lg font-semibold text-gray-800 truncate">Let&apos;s create your story</h2>
                <p className="text-xs text-gray-500">Step {currentStep} of {totalSteps} · {Math.round((currentStep / totalSteps) * 100)}%</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-full text-gray-500 hover:text-gray-700 hover:bg-gray-100 transition-colors flex-shrink-0"
              aria-label="Close"
            >
              ×
            </button>
          </div>
          <div className="px-4 md:px-6 pb-2">
            <div className="w-full bg-amber-100 rounded-full h-1.5">
              <div
                className="bg-amber-500 h-1.5 rounded-full transition-all duration-300"
                style={{ width: `${(currentStep / totalSteps) * 100}%` }}
              />
            </div>
          </div>
        </div>

        {/* Content — Step 1: compact height; Steps 2–4: full height as before */}
        <div className={`p-4 sm:p-6 overflow-y-auto ${currentStep === 1 ? 'max-h-[60vh] md:max-h-[58vh]' : 'flex-1 min-h-0'}`} style={currentStep !== 1 ? { maxHeight: 'min(calc(100dvh - 160px), calc(90vh - 160px))' } : undefined}>
          {/* Step 1: Story Title */}
          {currentStep === 1 && (
            <div className="text-center py-2 md:py-4">
              <div className="text-4xl md:text-5xl mb-2 md:mb-3">📝</div>
              <h3 className="text-xl md:text-2xl font-bold text-gray-800 mb-2 md:mb-3">What&apos;s Your Story Called?</h3>
              <p className="text-gray-600 mb-4 md:mb-5 text-sm md:text-base">Give your amazing story a fantastic title!</p>
              <input
                type="text"
                value={storyTitle}
                onChange={(e) => setStoryTitle(e.target.value)}
                placeholder="Enter your story title..."
                className="w-full max-w-md mx-auto p-3 md:p-4 border border-gray-200 rounded-xl text-base md:text-xl focus:border-slate-400 focus:ring-2 focus:ring-slate-200 focus:outline-none"
              />
            </div>
          )}

          {/* Step 2: Main Character */}
          {currentStep === 2 && (
            <div>
              <div className="text-center mb-6">
                <div className="text-6xl mb-4">⭐</div>
                <h3 className="text-2xl font-bold text-gray-800 mb-2">Who is Your Story About?</h3>
                <p className="text-gray-600">Tell us about your main character — the star of your story!</p>
              </div>
              
              {/* My Character Library Button */}
              <div className="flex justify-center mb-6">
                <button
                  onClick={() => {
                    loadUserCharacterLibrary(true);
                    setShowCharacterLibrary(true);
                  }}
                  className="bg-amber-500 hover:bg-amber-600 text-white font-semibold py-3 px-6 rounded-xl shadow-md transition-all duration-200 flex items-center space-x-2"
                >
                  <FaBook className="text-sm" />
                  <span>My Character Library</span>
                </button>
              </div>

              {/* Character Creation Form - Show only if no character is selected or confirmed */}
              {!characterConfirmed && !selectedCharacterFromLibrary && (
                <div className="bg-gray-50 rounded-xl p-4 sm:p-6 border border-gray-200">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-800 mb-2">Character Name</label>
                      <input
                        type="text"
                        value={mainCharacter.name}
                        onChange={(e) => setMainCharacter({...mainCharacter, name: e.target.value})}
                        placeholder="Enter character name..."
                        className="w-full p-3 border border-gray-200 rounded-lg focus:border-slate-400 focus:ring-1 focus:ring-slate-200 focus:outline-none"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-800 mb-2">Age</label>
                      <input
                        type="number"
                        value={mainCharacter.age}
                        onChange={(e) => setMainCharacter({...mainCharacter, age: e.target.value})}
                        placeholder="Enter age..."
                        className="w-full p-3 border border-gray-200 rounded-lg focus:border-slate-400 focus:ring-1 focus:ring-slate-200 focus:outline-none"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-800 mb-2">Gender</label>
                      <input
                        type="text"
                        value={mainCharacter.gender}
                        onChange={(e) => setMainCharacter({...mainCharacter, gender: e.target.value})}
                        placeholder="Enter gender..."
                        className="w-full p-3 border border-gray-200 rounded-lg focus:border-slate-400 focus:ring-1 focus:ring-slate-200 focus:outline-none"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-800 mb-2">Role in Story</label>
                      <select
                        value={mainCharacter.role}
                        onChange={(e) => setMainCharacter({...mainCharacter, role: e.target.value})}
                        className="w-full p-3 border border-gray-200 rounded-lg focus:border-slate-400 focus:ring-1 focus:ring-slate-200 focus:outline-none"
                      >
                        {ROLES.map(role => (
                          <option key={role} value={role}>{role}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-800 mb-2">Country of Origin</label>
                      <input
                        type="text"
                        value={mainCharacter.countryOfOrigin}
                        onChange={(e) => setMainCharacter({...mainCharacter, countryOfOrigin: e.target.value})}
                        placeholder="e.g., United States, Japan, France..."
                        className="w-full p-3 border border-gray-200 rounded-lg focus:border-slate-400 focus:ring-1 focus:ring-slate-200 focus:outline-none"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-800 mb-2">Define Character Face</label>
                      <textarea
                        value={mainCharacter.characterFace}
                        onChange={(e) => setMainCharacter({...mainCharacter, characterFace: e.target.value})}
                        placeholder="Describe the character's facial features: eyes, nose, mouth, facial shape, expressions, etc."
                        className="w-full p-3 border border-gray-200 rounded-lg focus:border-slate-400 focus:ring-1 focus:ring-slate-200 focus:outline-none min-h-[80px]"
                      />
                    </div>
                  </div>
                  
                  {/* Appearance field - full width */}
                  <div className="mt-4">
                    <label className="block text-sm font-semibold text-gray-800 mb-2">
                      👤 Character Appearance
                    </label>
                    <textarea
                      value={mainCharacter.appearance}
                      onChange={(e) => setMainCharacter({...mainCharacter, appearance: e.target.value})}
                      placeholder="Describe their appearance: hair color, clothing, distinctive features, etc..."
                      rows="3"
                      className="w-full p-3 border border-gray-200 rounded-lg focus:border-slate-400 focus:ring-1 focus:ring-slate-200 focus:outline-none"
                    />
                  </div>
                  {/* Create Character Button */}
                  <div className="mt-6 flex justify-center">
                    <button
                      onClick={() => {
                        console.log('=== BUTTON CLICKED ===');
                        console.log('Button disabled check:', {
                          isGeneratingCharacter,
                          hasName: !!mainCharacter.name,
                          hasAge: !!mainCharacter.age,
                          hasGender: !!mainCharacter.gender,
                          hasAppearance: !!mainCharacter.appearance,
                          hasCountry: !!mainCharacter.countryOfOrigin,
                          hasFace: !!mainCharacter.characterFace
                        });
                        handleCreateCharacter();
                      }}
                      disabled={isGeneratingCharacter || !mainCharacter.name || !mainCharacter.age || !mainCharacter.gender || !mainCharacter.appearance || !mainCharacter.countryOfOrigin || !mainCharacter.characterFace}
                      className={`px-4 md:px-8 py-2.5 md:py-4 rounded-lg md:rounded-xl font-semibold text-sm md:text-base transition-colors flex items-center justify-center space-x-2 ${
                        isGeneratingCharacter || !mainCharacter.name || !mainCharacter.age || !mainCharacter.gender || !mainCharacter.appearance || !mainCharacter.countryOfOrigin || !mainCharacter.characterFace
                          ? 'bg-amber-200 text-amber-600 cursor-not-allowed'
                          : 'bg-amber-500 hover:bg-amber-600 text-white'
                      }`}
                    >
                      {isGeneratingCharacter ? (
                        <>
                          <div className="animate-spin rounded-full h-3 w-3 md:h-5 md:w-5 border-b-2 border-white"></div>
                          <span>Creating Character...</span>
                        </>
                      ) : (
                        <>
                          <FaUser className="text-xs md:text-base" />
                          <span>Create Character</span>
                        </>
                      )}
                    </button>
                  </div>
                  
                  {/* Error Message */}
                  {characterCreationError && (
                    <div className="mt-4 p-3 bg-red-100 border border-red-300 rounded-lg">
                      <p className="text-red-700 text-sm">{characterCreationError}</p>
                    </div>
                  )}
                </div>
              )}

              {/* Selected Character Display - Show after selection; confirm separately */}
              {selectedCharacterFromLibrary && (
                <div className="bg-green-50 rounded-xl p-6 border-2 border-green-200">
                  <div className="text-center mb-4">
                    <h4 className="text-xl font-bold text-green-800 mb-2">Selected Character</h4>
                    <p className="text-green-600">This character will be the star of your story!</p>
                  </div>
                  
                  <div className="flex items-center space-x-6">
                    {/* Character Image - large so the character is clearly visible */}
                    <div className="w-48 h-48 sm:w-56 sm:h-56 flex-shrink-0 bg-green-100 rounded-xl flex items-center justify-center overflow-hidden border-2 border-green-200">
                      {selectedCharacterFromLibrary.imageUrl ? (
                        <img 
                          src={selectedCharacterFromLibrary.imageUrl} 
                          alt={selectedCharacterFromLibrary.name}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'flex';
                          }}
                        />
                      ) : null}
                      <FaUser className="text-green-600 text-4xl" style={{ display: selectedCharacterFromLibrary.imageUrl ? 'none' : 'flex' }} />
                    </div>
                    
                    {/* Character Details */}
                    <div className="flex-1">
                      <h5 className="text-2xl font-bold text-green-800 mb-2">{selectedCharacterFromLibrary.name}</h5>
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <div><strong>Age:</strong> {selectedCharacterFromLibrary.age}</div>
                        <div><strong>Gender:</strong> {selectedCharacterFromLibrary.gender}</div>
                        <div><strong>Role:</strong> {selectedCharacterFromLibrary.role}</div>
                        <div><strong>Country:</strong> {selectedCharacterFromLibrary.countryOfOrigin || 'Unknown'}</div>
                      </div>
                      {selectedCharacterFromLibrary.appearance && (
                        <div className="mt-2">
                          <strong>Appearance:</strong> 
                          <p className="text-sm text-green-700 mt-1">{selectedCharacterFromLibrary.appearance}</p>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {/* Action Buttons */}
                  <div className="mt-4 md:mt-6 flex flex-wrap justify-center gap-2 md:gap-4">
                    <button
                      onClick={() => { setCharacterConfirmed(true); setShowAddAnotherCharacter(true); }}
                      className="bg-amber-500 hover:bg-amber-600 text-white px-4 md:px-6 py-2 md:py-3 rounded-lg md:rounded-xl font-semibold text-sm md:text-base transition-colors"
                    >
                      Confirm Character
                    </button>
                    <button
                      onClick={() => setShowEditModalForSelected(true)}
                      className="bg-amber-500 hover:bg-amber-600 text-white px-4 md:px-6 py-2 md:py-3 rounded-lg md:rounded-xl font-semibold text-sm md:text-base transition-colors flex items-center gap-2"
                    >
                      <FaEdit /> Edit
                    </button>
                    <button
                      onClick={() => {
                        setCharacterConfirmed(false);
                        setSelectedCharacterFromLibrary(null);
                        setMainCharacter({
                          name: '', age: '', gender: '', role: 'Hero/Protagonist', appearance: '', countryOfOrigin: '', characterFace: ''
                        });
                        setShowEditModalForSelected(false);
                      }}
                      className="bg-amber-100 hover:bg-amber-200 text-amber-900 px-4 md:px-6 py-2 md:py-3 rounded-lg md:rounded-xl font-semibold text-sm md:text-base transition-colors"
                    >
                      Choose Different Character
                    </button>
                  </div>

                  {/* Same Edit Character modal as in preview — change character with prompt, then save */}
                  {showEditModalForSelected && selectedCharacterFromLibrary && (
                    <CharacterEditModal
                      isOpen={showEditModalForSelected}
                      onClose={() => setShowEditModalForSelected(false)}
                      character={{
                        ...selectedCharacterFromLibrary,
                        imageUrl: selectedCharacterFromLibrary.imageUrl?.startsWith('/uploads/')
                          ? `${API_ENDPOINTS.backendBase}${selectedCharacterFromLibrary.imageUrl}`
                          : selectedCharacterFromLibrary.imageUrl,
                      }}
                      onCharacterUpdate={(updated) => {
                        setSelectedCharacterFromLibrary(updated);
                        setShowEditModalForSelected(false);
                      }}
                    />
                  )}
                </div>
              )}
            </div>
          )}

          {/* Step 3: Additional Characters */}
          {currentStep === 3 && (
            <div>
              <div className="text-center mb-4 md:mb-6">
                <h3 className="text-lg md:text-2xl font-bold text-gray-800 mb-2">Who Are the Other Characters?</h3>
                <p className="text-sm md:text-base text-gray-600">Add more characters to make your story exciting!</p>
              </div>
              
              {/* Character List */}
              <div className="space-y-4 mb-6">
                {additionalCharacters.map((char, index) => (
                  <div key={char.id} className="bg-white border border-gray-200 rounded-xl p-4 flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center">
                        <FaUser className="text-gray-600" />
                      </div>
                      <div>
                        <div className="font-bold text-gray-800">{char.name}</div>
                        <div className="text-sm text-gray-600">{char.age} years old, {char.gender} • {char.role}</div>
                      </div>
                    </div>
                    <button
                      onClick={() => handleRemoveCharacter(char.id)}
                      className="text-red-500 hover:text-red-700"
                    >
                      <FaTrash />
                    </button>
                  </div>
                ))}
              </div>

              {/* Add Character Button */}
              {!showAddCharacter && (
                <button
                  onClick={() => {
                    // Navigate to Step 2 character selection window
                    setCurrentStep(2);
                    setShowAddCharacter(false);
                    setCharacterConfirmed(false);
                    setSelectedCharacterFromLibrary(null);
                    setMainCharacter({ 
                      name: '', 
                      age: '', 
                      gender: '', 
                      role: 'Hero/Protagonist', 
                      appearance: '',
                      countryOfOrigin: '',
                      characterFace: ''
                    });
                  }}
                  className="w-full bg-amber-100 hover:bg-amber-200 text-amber-900 font-semibold py-2.5 md:py-4 px-4 md:px-6 rounded-lg md:rounded-xl border-2 border-amber-300 transition-colors flex items-center justify-center space-x-2 text-sm md:text-base"
                >
                  <FaPlus className="text-xs md:text-base" />
                  <span>Add Another Character</span>
                </button>
              )}

              {/* Add Character Form */}
              {showAddCharacter && (
                <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
                  <h4 className="font-bold text-gray-800 mb-4">Add New Character</h4>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-800 mb-2">Character Name</label>
                      <input
                        type="text"
                        value={newCharacter.name}
                        onChange={(e) => setNewCharacter({...newCharacter, name: e.target.value})}
                        placeholder="Enter character name..."
                        className="w-full p-3 border border-gray-200 rounded-lg focus:border-slate-400 focus:ring-1 focus:ring-slate-200 focus:outline-none"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-800 mb-2">Age</label>
                      <input
                        type="number"
                        value={newCharacter.age}
                        onChange={(e) => setNewCharacter({...newCharacter, age: e.target.value})}
                        placeholder="Enter age..."
                        className="w-full p-3 border border-gray-200 rounded-lg focus:border-slate-400 focus:ring-1 focus:ring-slate-200 focus:outline-none"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-800 mb-2">Gender</label>
                      <input
                        type="text"
                        value={newCharacter.gender}
                        onChange={(e) => setNewCharacter({...newCharacter, gender: e.target.value})}
                        placeholder="Enter gender..."
                        className="w-full p-3 border border-gray-200 rounded-lg focus:border-slate-400 focus:ring-1 focus:ring-slate-200 focus:outline-none"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-800 mb-2">Role in Story</label>
                      <select
                        value={newCharacter.role}
                        onChange={(e) => setNewCharacter({...newCharacter, role: e.target.value})}
                        className="w-full p-3 border border-gray-200 rounded-lg focus:border-slate-400 focus:ring-1 focus:ring-slate-200 focus:outline-none"
                      >
                        <option value="">Select role...</option>
                        {ROLES.map(role => (
                          <option key={role} value={role}>{role}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  
                  {/* Appearance field - full width */}
                  <div className="mt-4">
                    <label className="block text-sm font-semibold text-gray-800 mb-2">
                      👤 Character Appearance
                    </label>
                    <textarea
                      value={newCharacter.appearance}
                      onChange={(e) => setNewCharacter({...newCharacter, appearance: e.target.value})}
                      placeholder="Describe their appearance: hair color, clothing, distinctive features, etc..."
                      rows="3"
                      className="w-full p-3 border border-gray-200 rounded-lg focus:border-slate-400 focus:ring-1 focus:ring-slate-200 focus:outline-none"
                    />
                  </div>
                  <div className="flex space-x-3 mt-4">
                    <button
                      onClick={handleAddCharacter}
                      className="bg-amber-500 hover:bg-amber-600 text-white px-6 py-2 rounded-lg font-semibold transition-colors"
                    >
                      Add Character
                    </button>
                    <button
                      onClick={() => setShowAddCharacter(false)}
                      className="bg-amber-100 hover:bg-amber-200 text-amber-900 px-6 py-2 rounded-lg font-semibold transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Step 4: Story Setting */}
          {currentStep === 4 && (
            <div>
              <div className="text-center mb-6">
                <div className="text-6xl mb-4">🌍</div>
                <h3 className="text-2xl font-bold text-gray-800 mb-2">What&apos;s Your Story?</h3>
                <p className="text-gray-600">Set the scene for your amazing story and tell us what it&apos;s all about!</p>
              </div>
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-800 mb-2">
                    <FaMapMarkerAlt className="inline mr-2" />
                    Story Location
                  </label>
                  <input
                    type="text"
                    value={storyLocation}
                    onChange={(e) => setStoryLocation(e.target.value)}
                    placeholder="e.g., A magical forest, Space station, Ancient castle..."
                    className="w-full p-4 border border-gray-200 rounded-xl focus:border-slate-400 focus:ring-2 focus:ring-slate-200 focus:outline-none text-lg"
                  />
                </div>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-800 mb-2">
                      <FaTheaterMasks className="inline mr-2" />
                      Genre
                    </label>
                    <select
                      value={selectedGenre}
                      onChange={(e) => setSelectedGenre(e.target.value)}
                      className="w-full p-4 border border-gray-200 rounded-xl focus:border-slate-400 focus:ring-1 focus:ring-slate-200 focus:outline-none text-lg"
                    >
                      <option value="">Select genre...</option>
                      {GENRES.map(genre => (
                        <option key={genre} value={genre}>{genre}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-800 mb-2">
                    ✏️ Give a brief about whats this story is about ?
                  </label>
                  <textarea
                    value={storySummary}
                    onChange={e => setStorySummary(e.target.value)}
                    placeholder="Tell us what happens in your story, the main idea, or a summary..."
                    className="w-full p-4 border border-gray-200 rounded-xl focus:border-slate-400 focus:ring-1 focus:ring-slate-200 focus:outline-none text-lg min-h-[120px]"
                  />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Navigation */}
        <div className="p-3 sm:p-4 md:p-6 border-t border-gray-200 flex flex-col md:flex-row justify-between gap-2 md:gap-0 flex-shrink-0 bg-white">
          <button
            onClick={handlePrevious}
            disabled={currentStep === 1}
            className={`px-4 md:px-6 py-2 md:py-3 rounded-lg md:rounded-xl font-semibold text-xs md:text-base transition-colors ${
              currentStep === 1
                ? 'bg-amber-200 text-amber-600 cursor-not-allowed'
                : 'bg-amber-100 hover:bg-amber-200 text-amber-900'
            }`}
          >
            Previous
          </button>
          
          {currentStep < totalSteps ? (
            <button
              onClick={handleNext}
              disabled={!canProceed()}
              className={`px-4 md:px-6 py-2 md:py-3 rounded-lg md:rounded-xl font-semibold text-xs md:text-base transition-colors ${
                !canProceed()
                  ? 'bg-amber-200 text-amber-600 cursor-not-allowed'
                  : 'bg-amber-500 hover:bg-amber-600 text-white'
              }`}
            >
              Next
            </button>
          ) : (
            <button
              onClick={handleComplete}
              disabled={!canProceed()}
              className={`px-4 md:px-6 py-2 md:py-3 rounded-lg md:rounded-xl font-semibold text-xs md:text-base transition-colors flex items-center justify-center space-x-2 ${
                !canProceed()
                  ? 'bg-amber-200 text-amber-600 cursor-not-allowed'
                  : 'bg-amber-500 hover:bg-amber-600 text-white'
              }`}
            >
              <FaPlay className="text-xs md:text-base" />
              <span>Start Storyboarding!</span>
            </button>
          )}
        </div>
      </div>

      {/* Character Preview Modal */}
      {showCharacterPreview && (
        <CharacterPreviewModal
          isOpen={showCharacterPreview}
          onClose={() => setShowCharacterPreview(false)}
          characterImage={characterImage}
          characterData={mainCharacter}
          onSave={handleCharacterPreviewSave}
          onEdit={handleCharacterPreviewEdit}
          onRegenerate={handleCharacterPreviewRegenerate}
          isGenerating={isGeneratingCharacter}
          error={characterCreationError}
        />
      )}

      {/* Character Library Modal */}
      {showCharacterLibrary && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-2 sm:p-4 overflow-hidden">
          <div className="bg-white rounded-none sm:rounded-xl md:rounded-2xl shadow-2xl w-full max-w-5xl max-h-[70vh] md:max-h-[65vh] overflow-hidden flex flex-col my-auto">
            <div className="flex-shrink-0 bg-white border-b border-gray-200 shadow-sm py-2 px-4 md:px-6">
              <div className="flex items-center justify-between">
                <h2 className="text-base md:text-lg font-semibold text-gray-800">My Character Library</h2>
                <button
                  onClick={() => setShowCharacterLibrary(false)}
                  className="p-2 rounded-full text-gray-500 hover:text-gray-700 hover:bg-gray-100 transition-colors"
                  aria-label="Close"
                >
                  ×
                </button>
              </div>
            </div>
            <div className="p-3 sm:p-4 overflow-x-auto overflow-y-hidden flex-1 min-h-0">
              <h3 className="text-base sm:text-lg font-bold text-gray-800 mb-1 sm:mb-2">Your Characters</h3>
              <p className="text-xs sm:text-sm text-gray-600 mb-3">Scroll horizontally to browse · Double-click to select a character</p>
              {characterLibraryLoading ? (
                <p className="text-gray-600">Loading your characters...</p>
              ) : characterLibraryError ? (
                <div className="text-gray-700">
                  <p className="mb-2">{characterLibraryError}</p>
                  <button type="button" onClick={() => loadUserCharacterLibrary(true)} className="text-slate-600 underline font-medium">Try again</button>
                </div>
              ) : userCharacters.length === 0 ? (
                <p className="text-gray-600">You haven&apos;t created any characters yet. Add one to your library!</p>
              ) : (
                <div className="overflow-x-auto overflow-y-hidden">
                  <div className="flex space-x-4 pb-3" style={{ minWidth: 'max-content' }}>
                    {userCharacters.map((character, index) => (
                      <div
                        key={character.id}
                        className="flex-shrink-0 w-32 sm:w-40 cursor-pointer group relative"
                        onClick={() => handleCharacterSelectionFromLibrary(character)}
                        onDoubleClick={() => handleCharacterSelectionFromLibrary(character)}
                      >
                        <div className="bg-gray-50 border border-gray-200 rounded-lg p-2.5 sm:p-3 hover:border-slate-300 hover:shadow-md transition-all duration-300 group-hover:scale-[1.03]">

                          <div className="w-full h-28 sm:h-32 bg-gray-100 rounded-md flex items-center justify-center overflow-hidden border border-gray-200 mb-2">
                            {character.imageUrl ? (
                              <img 
                                src={character.imageUrl} 
                                alt={character.name}
                                className="w-full h-full object-cover"
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                  e.target.nextSibling.style.display = 'flex';
                                }}
                              />
                            ) : null}
                            <FaUser className="text-gray-500 text-2xl sm:text-3xl" style={{ display: character.imageUrl ? 'none' : 'flex' }} />
                          </div>
                          <div className="text-center">
                            <div className="font-bold text-gray-800 text-xs sm:text-sm truncate">{character.name}</div>
                            <div className="text-[11px] sm:text-xs text-gray-600">{character.age} · {character.gender}</div>
                            
                            {/* Subtle Delete Icon */}
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                console.log('Delete button clicked for character:', character);
                                console.log('Character ID options:', { id: character.id, characterId: character.characterId, docId: character.docId });
                                const characterId = character.id || character.characterId || character.docId;
                                console.log('Selected character ID:', characterId);
                                if (!characterId) {
                                  console.error('No character ID found for character:', character);
                                  alert('Error: Character ID not found. Please try refreshing the page.');
                                  return;
                                }
                                handleDeleteCharacterFromLibrary(characterId, character.name);
                              }}
                              className="absolute top-1 right-1 bg-red-400 hover:bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs opacity-60 hover:opacity-100 transition-all duration-200 z-10"
                              title={`Delete ${character.name}`}
                            >
                              <FaTrash className="text-xs" />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <div className="py-2 px-3 sm:px-4 border-t border-gray-200 flex justify-end flex-shrink-0 bg-white">
              <button
                onClick={() => setShowCharacterLibrary(false)}
                className="bg-amber-500 hover:bg-amber-600 text-white px-6 py-2.5 rounded-xl font-semibold transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <CustomModal
        isOpen={showDeleteConfirmation}
        onClose={() => {
          setShowDeleteConfirmation(false);
          setCharacterToDelete(null);
        }}
        onConfirm={confirmDeleteCharacter}
        title={
          characterToDelete?.success 
            ? "Character Deleted Successfully!" 
            : characterToDelete?.error 
              ? "Delete Failed" 
              : "Delete Character"
        }
        message={
          characterToDelete?.success 
            ? `"${characterToDelete.name}" has been successfully removed from your character library.`
            : characterToDelete?.error 
              ? `Failed to delete "${characterToDelete.name}". Please try again.`
              : `Are you sure you want to delete "${characterToDelete?.name}" from your character library? This action cannot be undone.`
        }
        confirmText={
          characterToDelete?.success || characterToDelete?.error 
            ? "OK" 
            : "Delete"
        }
        cancelText={
          characterToDelete?.success || characterToDelete?.error 
            ? null 
            : "Cancel"
        }
        type={
          characterToDelete?.success 
            ? "success" 
            : characterToDelete?.error 
              ? "error" 
              : "warning"
        }
      />

      {/* Add Another Character Modal */}
      {showAddAnotherCharacter && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-3 sm:p-4 overflow-auto">
          <div className="bg-white rounded-xl md:rounded-2xl shadow-2xl max-w-md w-full p-4 sm:p-6 my-auto">
            <div className="text-center">
              <div className="text-4xl mb-4">🎭</div>
              <h3 className="text-xl font-bold text-gray-800 mb-4">Character Selected Successfully!</h3>
              
              {/* Show currently selected characters */}
              {selectedCharacters.length > 0 && (
                <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-sm font-semibold text-gray-800">Currently Selected Characters ({selectedCharacters.length}):</p>
                    <button
                      onClick={() => setSelectedCharacters([])}
                      className="text-red-500 hover:text-red-700 text-xs font-medium px-2 py-1 rounded hover:bg-red-50 transition-colors"
                    >
                      Remove All
                    </button>
                  </div>
                  <div className="space-y-1">
                    {selectedCharacters.map((character, index) => (
                      <div key={character.id || index} className="text-xs text-gray-700 flex items-center justify-center">
                        <span className="w-4 h-4 bg-slate-400 rounded-full mr-2 flex-shrink-0"></span>
                        {character.name} ({character.role || 'Character'})
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRemoveSelectedCharacter(character);
                          }}
                          className="ml-2 text-red-500 hover:text-red-700 text-xs"
                          title="Remove from selected"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              <p className="text-gray-600 mb-6">Would you like to add another character to your story?</p>
              
              <div className="flex flex-col md:flex-row gap-2 md:gap-4">
                <button
                  onClick={() => handleAddAnotherCharacterDecision(true)}
                  className="flex-1 bg-amber-500 hover:bg-amber-600 text-white font-semibold py-2.5 md:py-3 px-4 md:px-6 rounded-lg md:rounded-xl text-sm md:text-base transition-colors"
                >
                  Add Another
                </button>
                <button
                  onClick={() => handleAddAnotherCharacterDecision(false)}
                  className="flex-1 bg-amber-100 hover:bg-amber-200 text-amber-900 font-semibold py-2.5 md:py-3 px-4 md:px-6 rounded-lg md:rounded-xl text-sm md:text-base transition-colors"
                >
                  Continue
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Artmosphere-style success overlay (amber/warm, not green) */}
      {showSuccessNotification && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-3 sm:p-4 pointer-events-none">
          <div className="bg-gradient-to-r from-amber-500 to-amber-400 rounded-xl md:rounded-2xl shadow-2xl p-4 sm:p-6 max-w-md w-full transform transition-all duration-500 ease-out pointer-events-auto animate-bounce-in border border-amber-200/50">
            <div className="text-center">
              {/* Success Icon */}
              <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <div className="text-3xl">✨</div>
              </div>
              
              {/* Success Message */}
              <h3 className="text-xl font-bold text-white mb-2">Character saved</h3>
              <p className="text-white/90 text-sm mb-4">Your character has been saved to your library. You can proceed to the next step.</p>
              
              {/* Artmosphere Branding */}
              <div className="flex items-center justify-center space-x-2 text-white/80 text-xs">
                <div className="w-2 h-2 bg-white/60 rounded-full"></div>
                <span>Artmosphere</span>
                <div className="w-2 h-2 bg-white/60 rounded-full"></div>
              </div>
            </div>
            
            {/* Close Button */}
            <button
              onClick={() => setShowSuccessNotification(false)}
              className="absolute top-2 right-2 text-white/70 hover:text-white text-xl transition-colors duration-200"
            >
              ×
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

StorySetupModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onStorySetupComplete: PropTypes.func.isRequired
};